import { createContext, type ReactNode, useState } from 'react';

import { type KycSurvey } from '@/types/know-your-customer-service';

export type KycSectionProviderProps = {
  children: ReactNode;
  initial?: KycSectionInfo;
};

export type KycSectionInfo = {
  kycGroup: number;
  kycQuestion: number;
};

export const KycSectionContext = createContext<
  {
    navigateToNextGroup: (kycSurvey: KycSurvey) => void;
    navigateToNextQuestion: (kycSurvey: KycSurvey) => void;
    navigateToPreviousGroup: (kycSurvey: KycSurvey) => void;
    navigateToPreviousQuestion: () => void;
  } & KycSectionInfo
>({
  kycGroup: 0,
  kycQuestion: 0,
  navigateToNextGroup: () => {},
  navigateToNextQuestion: () => {},
  navigateToPreviousGroup: () => {},
  navigateToPreviousQuestion: () => {},
});

export const KycSectionProvider = ({
  children,
  initial,
}: KycSectionProviderProps) => {
  const [{ kycGroup, kycQuestion }, navigateToQuestion] =
    useState<KycSectionInfo>(initial || { kycGroup: 0, kycQuestion: 0 });
  const navigateToPreviousQuestion = () => {
    if (kycQuestion > 0)
      navigateToQuestion({ kycGroup, kycQuestion: kycQuestion - 1 });
    else throw new Error('No previous question available');
  };
  const navigateToPreviousGroup = (kycSurvey: KycSurvey) => {
    if (kycGroup > 0) {
      const previousKycGroup = kycGroup - 1;
      const previousSectionQuestion = kycSurvey.groups[
        previousKycGroup
      ].questions.filter((question) => !question.displayCondition);

      navigateToQuestion({
        kycGroup: previousKycGroup,
        kycQuestion: previousSectionQuestion.length - 1,
      });
    } else {
      throw new Error('No previous group available');
    }
  };

  const navigateToNextGroup = (kycSurvey: KycSurvey) => {
    if (kycGroup < kycSurvey.groups.length - 1) {
      const nextKycGroup = kycGroup + 1;

      navigateToQuestion({
        kycGroup: nextKycGroup,
        kycQuestion: 0,
      });
    } else {
      throw new Error('No next group available');
    }
  };

  const navigateToNextQuestion = (kycSurvey: KycSurvey) => {
    const currentGroupQuestions = kycSurvey.groups[kycGroup].questions.filter(
      (question) => !question.displayCondition,
    );

    if (kycQuestion + 1 < currentGroupQuestions.length)
      navigateToQuestion({ kycGroup, kycQuestion: kycQuestion + 1 });
    else throw new Error('No next question available');
  };

  return (
    <KycSectionContext.Provider
      value={{
        kycGroup,
        kycQuestion,
        navigateToNextGroup,
        navigateToNextQuestion,
        navigateToPreviousGroup,
        navigateToPreviousQuestion,
      }}
    >
      {children}
    </KycSectionContext.Provider>
  );
};
